import { BookingCardTheme } from './themesCrafted';
import { CheckCircle2, Hash, Instagram } from 'lucide-react';
import { formatMemberSince } from '@/utils/dateFormatting';
import { motion } from 'framer-motion';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useFitText } from '@/hooks/useFitText';

export interface BookingCardFrontProps {
  businessName: string;
  businessInstagramHandle: string | null;
  businessLogoUrl?: string | null;
  clientFullName: string;
  displayedVisits: number;
  memberSinceDate: string | null;
  publicConfirmationCode: string;
  theme: BookingCardTheme;
  tileAnimation?: {
    enabled?: boolean;
    activeOpacity?: number;
    inactiveOpacity?: number;
    speedMultiplier?: number;
    rotationIntensity?: number;
  };
  centerImageUrl?: string | null;
  centerCustomText?: string;
}

/**
 * Membership Card Style - Inspired by Bad Barbers
 *
 * Design Principles:
 * - Clean, minimal design with strong visual identity
 * - Monogram/logo as central element
 * - Satoshi font for modern, professional feel
 * - Balanced information hierarchy
 */
export function BookingCardFrontCrafted({
  businessName,
  businessInstagramHandle,
  businessLogoUrl,
  clientFullName,
  displayedVisits,
  memberSinceDate,
  publicConfirmationCode,
  theme,
  tileAnimation,
  centerImageUrl,
  centerCustomText,
}: BookingCardFrontProps) {
  const memberSince = formatMemberSince(memberSinceDate);
  const prefersReducedMotion = useReducedMotion();
  const { ref: nameRef, fontSize: nameFontSize } = useFitText(clientFullName, 24, 10);
  const { ref: bizRef, fontSize: bizFontSize } = useFitText(businessName, 18, 10);

  // Tile animation settings with defaults
  const tilesEnabled = tileAnimation?.enabled ?? true;
  const activeOpacity = tileAnimation?.activeOpacity ?? 0.22;
  const inactiveOpacity = tileAnimation?.inactiveOpacity ?? 0.21;
  const speedMultiplier = tileAnimation?.speedMultiplier ?? 1.0;
  const rotationIntensity = tileAnimation?.rotationIntensity ?? 15;

  // Generate monogram from business name (first letters of first two words)
  const generateMonogram = (name: string): string => {
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      // Single word: use first two letters
      return words[0].substring(0, 2).toUpperCase();
    }
    // Multiple words: use first letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Determine center content: custom text > logo > custom image > auto monogram
  const displayImageUrl = businessLogoUrl || centerImageUrl;
  const centerContent = centerCustomText || (displayImageUrl ? null : generateMonogram(businessName));

  // Generate visit visualization grid
  const generateVisitGrid = (visits: number): boolean[] => {
    const maxDots = 48; // 6 columns × 8 rows
    const grid: boolean[] = [];

    // Exact match: 3 visits = 3 tiles lit
    for (let i = 0; i < maxDots; i++) {
      grid.push(i < visits);
    }

    return grid;
  };

  const visitGrid = generateVisitGrid(displayedVisits);

  return (
    <div
      className="w-full h-full rounded-3xl p-6 flex flex-col relative overflow-hidden font-['Satoshi']"
      style={{
        background: theme.gradientFrom,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16), 0 12px 40px rgba(0, 0, 0, 0.24), 0 0 1px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Background Visit Visualization - 3D flipping tiles with depth */}
      {tilesEnabled && (
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          perspective: '1000px',
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            maskImage: 'radial-gradient(ellipse at center, transparent 25%, black 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, transparent 25%, black 75%)',
          }}
        >
          <div
            className="grid grid-cols-6 gap-2 p-6 h-full content-center"
            style={{ transformStyle: 'preserve-3d' }}
          >
          {visitGrid.map((active, i) => {
            // Wave pattern: stagger based on row + column position
            const row = Math.floor(i / 6);
            const col = i % 6;
            const staggerDelay = (row * 0.08) + (col * 0.05); // Wave from top-left to bottom-right

            // Organic 3D motion - each tile has unique rhythm
            const baseDuration = 10 / speedMultiplier;
            const floatDuration = baseDuration + (i % 5) * (1.2 / speedMultiplier); // Adjustable speed
            const floatDelay = i * 0.05; // 50ms stagger per tile

            if (prefersReducedMotion || !tilesEnabled) {
              // Static fallback for accessibility
              return (
                <div
                  key={i}
                  className="aspect-square rounded-[3px]"
                  style={{
                    backgroundColor: active ? theme.accentColor : theme.borderColor,
                    opacity: active ? activeOpacity : inactiveOpacity,
                  }}
                />
              );
            }

            return (
              <motion.div
                key={i}
                className="aspect-square rounded-[3px]"
                style={{
                  transformStyle: 'preserve-3d',
                }}
                initial={{
                  opacity: 0,
                  scale: 0.8,
                }}
                animate={{
                  opacity: active ? activeOpacity : inactiveOpacity,
                  scale: 1,
                }}
                transition={{
                  opacity: {
                    duration: 0.5,
                    delay: staggerDelay,
                    ease: 'easeOut',
                  },
                  scale: {
                    duration: 0.5,
                    delay: staggerDelay,
                    ease: [0.34, 1.56, 0.64, 1],
                  },
                }}
              >
                {/* Continuous 3D corner-tilt animation */}
                <motion.div
                  className="w-full h-full rounded-[3px]"
                  style={{
                    backgroundColor: active ? theme.accentColor : theme.borderColor,
                    transformStyle: 'preserve-3d',
                  }}
                  animate={{
                    rotateX: [0, rotationIntensity, 0, -rotationIntensity, 0],
                    rotateY: [0, rotationIntensity, 0, -rotationIntensity, 0],
                    translateZ: [0, 4, 0, 4, 0],    // Subtle depth pulse
                  }}
                  transition={{
                    duration: floatDuration,
                    repeat: Infinity,
                    delay: staggerDelay + 0.6 + floatDelay,
                    ease: 'easeInOut',
                  }}
                />
              </motion.div>
            );
          })}
          </div>
        </div>
      </div>
      )}

      {/* Subtle depth effect - inner shadow */}
      <div
        className="absolute inset-0 rounded-3xl pointer-events-none"
        style={{
          boxShadow: `inset 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 -1px 2px rgba(255, 255, 255, 0.05)`,
        }}
      />
      {/* Top Header: Business Name + Visit Badge */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 pr-2">
          <div className="flex items-center gap-2">
            <div
              ref={bizRef}
              className="font-bold tracking-tight leading-tight"
              style={{
                color: theme.textPrimary,
                fontSize: `${bizFontSize}px`,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {businessName}
            </div>
            {businessInstagramHandle && (
              <a
                href={`https://instagram.com/${businessInstagramHandle.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 hover:opacity-70 transition-opacity"
                aria-label={`Visit ${businessName} on Instagram`}
              >
                <Instagram
                  style={{
                    color: theme.textSecondary,
                    width: 'clamp(0.875rem, 1.5vw, 1rem)',
                    height: 'clamp(0.875rem, 1.5vw, 1rem)',
                  }}
                />
              </a>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end flex-shrink-0">
          <div className="flex items-center gap-1">
            <div
              className="font-bold tabular-nums leading-none"
              style={{
                color: theme.textPrimary,
                fontSize: 'clamp(1.125rem, 2.5vw + 0.5rem, 1.25rem)',
              }}
            >
              {displayedVisits}
            </div>
            <CheckCircle2
              style={{
                color: theme.accentColor,
                width: 'clamp(1rem, 2vw, 1.25rem)',
                height: 'clamp(1rem, 2vw, 1.25rem)',
              }}
              fill="currentColor"
            />
          </div>
          <div
            className="uppercase tracking-wider font-medium mt-0.5"
            style={{
              color: theme.textSecondary,
              fontSize: 'clamp(0.625rem, 1.25vw, 0.75rem)',
            }}
          >
            VISITS
          </div>
        </div>
      </div>

      {/* Center: Logo, Image, Custom Text, or Auto Monogram */}
      <div className="flex-1 flex items-center justify-center my-6 relative">
        {displayImageUrl ? (
          /* Business Logo or Uploaded Image */
          <img
            src={displayImageUrl}
            alt={businessLogoUrl ? `${businessName} logo` : "Center logo"}
            className="max-h-32 sm:max-h-40 w-auto object-contain"
            style={{
              maxWidth: '100%',
              filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
            }}
            onError={(e) => {
              console.error('Logo failed to load:', displayImageUrl);
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <>
            {/* Text shadow/outline effect */}
            <div
              className="absolute font-black leading-none tracking-tighter blur-sm"
              style={{
                color: theme.accentColor,
                fontFamily: 'Satoshi, sans-serif',
                fontStyle: 'italic',
                opacity: 0.15,
                transform: 'translate(2px, 2px)',
                fontSize: 'clamp(3.5rem, 10vw + 1rem, 6rem)',
              }}
            >
              {centerContent}
            </div>
            {/* Main text (monogram or custom) */}
            <div
              className="relative font-black leading-none tracking-tighter"
              style={{
                color: theme.accentColor,
                fontFamily: 'Satoshi, sans-serif',
                fontStyle: 'italic',
                opacity: 0.95,
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                fontSize: 'clamp(3.5rem, 10vw + 1rem, 6rem)',
              }}
            >
              {centerContent}
            </div>
          </>
        )}
      </div>

      {/* Bottom: Client Info + Member Since + Confirmation Code */}
      <div className="mt-auto flex justify-between items-end gap-2">
        <div className="flex-1 min-w-0">
          <div
            ref={nameRef}
            className="font-bold uppercase tracking-wide mb-1"
            style={{
              color: theme.textPrimary,
              fontSize: `${nameFontSize}px`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {clientFullName}
          </div>
          {memberSince && (
            <div
              className="uppercase tracking-wider mb-2"
              style={{
                color: theme.textSecondary,
                fontSize: 'clamp(0.625rem, 1.25vw, 0.75rem)',
              }}
            >
              MEMBER SINCE: {memberSince}
            </div>
          )}
          <div className="flex items-center gap-1.5 min-w-0">
            <Hash
              className="flex-shrink-0"
              style={{
                color: theme.textSecondary,
                width: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                height: 'clamp(0.75rem, 1.5vw, 0.875rem)',
              }}
            />
            <div
              className="font-medium tracking-wide truncate"
              style={{
                color: theme.textSecondary,
                fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
              }}
            >
              {publicConfirmationCode}
            </div>
          </div>
        </div>
        <div
          className="font-medium tracking-wide text-right flex-shrink-0 whitespace-nowrap"
          style={{
            fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
            color: theme.textSecondary,
          }}
        >
          MADE WITH FIGGY
        </div>
      </div>
    </div>
  );
}
